import React, { useContext, useEffect, useRef, useState } from 'react';
import { Alert, Button, Col, Container, Modal, Row } from 'react-bootstrap';
import { FaBookOpen, FaCheckCircle, FaPlayCircle, FaRegCircle } from 'react-icons/fa';
import { useSearchParams } from 'react-router-dom';

import { AppContext } from '../../Context';
import useProgresoEvaluaciones from '../../hooks/useProgresoEvaluaciones';
import { ConsultaEvaluacionContext, esAlumnoDeEvaluacion } from '../../hooks/useModoConsultaEvaluacion';
import { instalarCapturaErroresEnvio } from '../../utils/registroErroresEjercicios';
import '../../routes/EvaluacionBasica.scss';

const obtenerTituloCorto = (nombre) => {
  const partes = nombre.split(' - ');
  return partes.length > 1 ? partes.slice(1).join(' - ') : nombre;
};

const ESTADO_CONFIG_VALIDOS = ['pendiente', 'en-curso', 'completado'];

const getEstadoConfig = (estado) => {
  if (estado === 'completado') {
    return {
      texto: 'Completado',
      boton: 'Repasar',
      chipClass: 'estado-chip-completado',
      buttonClass: 'evaluacion-btn-repasar',
      icono: <FaCheckCircle />,
    };
  }

  if (estado === 'en-curso') {
    return {
      texto: 'En curso',
      boton: 'Continuar',
      chipClass: 'estado-chip-en-curso',
      buttonClass: 'evaluacion-btn-continuar',
      icono: <FaPlayCircle />,
    };
  }

  return {
    texto: 'Pendiente',
    boton: 'Realizar ejercicio',
    chipClass: 'estado-chip-pendiente',
    buttonClass: 'evaluacion-btn-pendiente',
    icono: <FaRegCircle />,
  };
};

const EvaluacionNivelView = ({ nivel, titulo, modulos }) => {
  const { currentUser } = useContext(AppContext);
  const [searchParams, setSearchParams] = useSearchParams();
  const [modalShow, setModalShow] = useState(false);
  const [ejercicioActivo, setEjercicioActivo] = useState(null);
  const [ejercicioDestacado, setEjercicioDestacado] = useState('');
  const ejercicioRefs = useRef({});
  const esAlumno = esAlumnoDeEvaluacion(currentUser);
  const modoConsulta = searchParams.get('modo') === 'consulta' || !esAlumno;
  const estadoConsultado = searchParams.get('estado');
  const { progreso, iniciarEjercicio, completarEjercicio, errorProgreso } = useProgresoEvaluaciones(
    nivel,
    { habilitado: esAlumno && !modoConsulta }
  );

  useEffect(() => {
    const clave = searchParams.get('ejercicio');
    const elemento = clave ? ejercicioRefs.current[clave] : null;
    if (!elemento) return undefined;

    setEjercicioDestacado(clave);
    elemento.scrollIntoView({ behavior: 'smooth', block: 'center' });

    const timeoutId = window.setTimeout(() => {
      setEjercicioDestacado('');
      const siguientesParametros = new URLSearchParams(searchParams);
      siguientesParametros.delete('ejercicio');
      setSearchParams(siguientesParametros, { replace: true });
    }, 5000);

    return () => window.clearTimeout(timeoutId);
  }, [searchParams, setSearchParams]);

  const cerrarModal = () => {
    setModalShow(false);
    setEjercicioActivo(null);
  };

  const abrirModal = (ejercicio, modulo) => {
    setEjercicioActivo({ ...ejercicio, modulo });
    setModalShow(true);
    if (!modoConsulta) iniciarEjercicio(ejercicio, modulo);
  };

  const finalizarEjercicio = () => {
    if (ejercicioActivo && !modoConsulta) {
      completarEjercicio(ejercicioActivo, ejercicioActivo.modulo);
    }
    cerrarModal();
  };

  const ComponenteActivo = ejercicioActivo?.componente;

  useEffect(() => {
    if (!ejercicioActivo || modoConsulta) return undefined;

    return instalarCapturaErroresEnvio(() => ({
      alumnoId: currentUser?.uid,
      correoAlumno: currentUser?.email,
      nombreAlumno: currentUser?.nombre || currentUser?.displayName,
      nivel,
      modulo: ejercicioActivo.modulo?.replace('Modulo', ''),
      ejercicio: ejercicioActivo.nombre,
      ejercicioId: ejercicioActivo.id,
    }));
  }, [currentUser, ejercicioActivo, nivel, modoConsulta]);

  return (
    <Container className="evaluacion-layout pb-4">
      <h1 className="evaluacion-nivel-titulo text-center mt-4 mb-4">{titulo}</h1>

      {modoConsulta && (
        <Alert variant="info" className="evaluacion-consulta-alert">
          <strong>Vista de revisión.</strong> Puedes recorrer los pasos y probar los campos sin completarlos obligatoriamente. No se guardan respuestas ni progreso.
        </Alert>
      )}

      {errorProgreso && <Alert variant="danger">{errorProgreso}</Alert>}

      {Object.entries(modulos).map(([modulo, evaluaciones]) => {
        const completadasModulo = evaluaciones.filter(
          (item) => progreso[item.clave]?.estado === 'completado'
        ).length;
        const progresoModulo = evaluaciones.length === 0
          ? 0
          : Math.round((completadasModulo / evaluaciones.length) * 100);
        const moduloCompletado = progresoModulo === 100;

        return (
          <section key={modulo} className="modulo-section">
            <div className="modulo-header">
              <div className="modulo-title-wrap">
                <span className="modulo-icon"><FaBookOpen /></span>
                <h2>{modulo.replace('Modulo', 'Módulo ')}</h2>
              </div>

              <div className={`modulo-progress-wrap ${moduloCompletado ? 'is-complete' : ''}`}>
                <div className="modulo-progress-track" aria-hidden="true">
                  <div className="modulo-progress-fill" style={{ width: `${progresoModulo}%` }} />
                </div>
                <span className="modulo-progress-label">
                  {moduloCompletado && <FaCheckCircle aria-hidden="true" />}
                  {progresoModulo}% completado
                </span>
              </div>
            </div>

            <Row xs={1} md={2} lg={3} className="g-4 mb-4">
              {evaluaciones.map((evaluacion) => {
                const estado = modoConsulta
                  && ejercicioDestacado === evaluacion.clave
                  && ESTADO_CONFIG_VALIDOS.includes(estadoConsultado)
                  ? estadoConsultado
                  : progreso[evaluacion.clave]?.estado || 'pendiente';
                const estadoConfig = getEstadoConfig(estado);

                return (
                  <Col key={evaluacion.clave}>
                    <article
                      ref={(elemento) => {
                        if (elemento) ejercicioRefs.current[evaluacion.clave] = elemento;
                        else delete ejercicioRefs.current[evaluacion.clave];
                      }}
                      className={`evaluacion-card ${estado} ${ejercicioDestacado === evaluacion.clave ? 'is-direct-target' : ''}`}
                    >
                      <div className={`estado-chip ${estadoConfig.chipClass}`}>
                        <span className="estado-chip-icon">{estadoConfig.icono}</span>
                        <span>{estadoConfig.texto}</span>
                      </div>

                      <div className="evaluacion-meta">
                        <span className="evaluacion-id">ID : {evaluacion.id}</span>
                        <h3>{obtenerTituloCorto(evaluacion.nombre)}</h3>
                      </div>

                      <Button
                        className={`w-100 evaluacion-btn ${estadoConfig.buttonClass}`}
                        onClick={() => abrirModal(evaluacion, modulo)}
                      >
                        {modoConsulta ? 'Revisar ejercicio' : estadoConfig.boton}
                      </Button>
                    </article>
                  </Col>
                );
              })}
            </Row>
          </section>
        );
      })}

      <Modal show={modalShow} onHide={cerrarModal} size="xl" centered>
        <Modal.Header closeButton>
          <Modal.Title>{ejercicioActivo?.nombre || 'Ejercicio en curso'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {modoConsulta && (
            <Alert variant="secondary" className="mb-3">
              Revisión sin guardado: navega libremente y prueba las vistas. Las respuestas y el progreso sólo se guardan cuando el alumno realiza el ejercicio con su propia cuenta.
            </Alert>
          )}
          <ConsultaEvaluacionContext.Provider value={modoConsulta}>
            <fieldset className="evaluacion-preview-fieldset">
              {ComponenteActivo && <ComponenteActivo cerrarModal={finalizarEjercicio} />}
            </fieldset>
          </ConsultaEvaluacionContext.Provider>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={cerrarModal}>Cerrar</Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default EvaluacionNivelView;
